import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { colors } from "../../../constants/colors";

/**
 * สไตล์ชุดใหม่ของการ์ดประกาศข่าว (หน้าหลัก + หน้ารายละเอียด)
 * ใช้ colors.themeMainColor เป็นสีหลัก จะได้เปลี่ยนตาม role เหมือนหน้าอื่นในระบบ
 * (ผู้เช่า = เขียว, เจ้าหน้าที่ = น้ำเงิน) ไม่ hardcode สีจาก mockup
 */

/** การ์ดขาวมุมมน + แถบสีด้านซ้าย */
export const AnnounceCardShell = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: colors.white,
  border: `1px solid ${colors.black_08}`,
  borderLeft: `6px solid ${colors.themeMainColor}`,
  borderRadius: "16px",
  boxShadow: "0 2px 12px rgba(20, 55, 138, 0.06)",
  padding: "24px 28px",
  width: "100%",
  textAlign: "left",
  [theme.breakpoints.down("md")]: {
    padding: "20px 18px",
    borderRadius: "12px",
  },
}));

/** การ์ดขาวมุมมนแบบไม่มีแถบซ้าย ใช้เป็นกล่องใหญ่ของหน้ารายละเอียด */
export const AnnouncePanel = styled(Box)(({ theme }) => ({
  backgroundColor: colors.white,
  border: `1px solid ${colors.black_08}`,
  borderRadius: "16px",
  boxShadow: "0 2px 12px rgba(20, 55, 138, 0.06)",
  width: "100%",
  overflow: "hidden",
  textAlign: "left",
}));

/** ป้ายประเภทประกาศ (pill) */
export const AnnounceChip = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  backgroundColor: `${colors.themeMainColor}14`,
  color: colors.themeMainColor,
  borderRadius: "999px",
  padding: "5px 14px",
  fontFamily: "Kanit",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: 1.4,
  whiteSpace: "nowrap",
}));

export const AnnounceTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Kanit",
  fontSize: "22px",
  fontWeight: 600,
  color: colors.themeMainColor,
  lineHeight: 1.45,
  [theme.breakpoints.down("md")]: {
    fontSize: "18px",
  },
}));

export const MetaLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "Kanit",
  fontSize: "14px",
  fontWeight: 400,
  color: colors.textLightGray,
  lineHeight: 1.5,
}));

export const MetaValue = styled(Typography)(({ theme }) => ({
  fontFamily: "Kanit",
  fontSize: "16px",
  fontWeight: 500,
  color: colors.textPrimary,
  lineHeight: 1.5,
  wordBreak: "break-word",
}));

/** หัวข้อย่อยในหน้ารายละเอียด เช่น "รายละเอียดประกาศ" / "ไฟล์แนบ" */
export const SectionHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Kanit",
  fontSize: "17px",
  fontWeight: 600,
  color: colors.textPrimary,
  marginBottom: "12px",
}));

/** แถวไฟล์แนบ */
export const FileRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  border: `1px solid ${colors.black_08}`,
  borderRadius: "10px",
  backgroundColor: colors.white,
  padding: "10px 14px",
  marginBottom: "10px",
}));

/** ปุ่มลูกศรกลมของ carousel */
export const CarouselArrow = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 2,
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  backgroundColor: colors.white,
  border: `1px solid ${colors.black_08}`,
  boxShadow: "0 2px 8px rgba(20, 55, 138, 0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: colors.textLightGray,
  transition: "0.2s",
  ":hover": {
    color: colors.themeMainColor,
    borderColor: colors.themeMainColor,
  },
  [theme.breakpoints.down("sm")]: {
    width: "32px",
    height: "32px",
  },
}));

/**
 * ครอบ carousel ข่าวสารหน้าหลัก
 * .carousel.carousel-slider ของ library ตั้ง overflow:hidden ไว้ ดันปุ่มลูกศรออกนอกกรอบไม่ได้
 * จึงเว้นที่ด้วย SlidePadding ที่ตัวการ์ดแทน แล้ววางลูกศรที่ left/right = 0 ให้ไปอยู่ในช่องว่างนั้น
 */
export const CarouselFrame = styled(Box)(({ theme }) => ({
  position: "relative",
  /** ปิด style เริ่มต้นของ react-responsive-carousel ที่ทับ layout ใหม่ */
  ".carousel .slider-wrapper": { backgroundColor: "transparent" },
  ".carousel .slide": { backgroundColor: "transparent" },
  /** ย้ายจุดบอกสไลด์ลงมาไว้ใต้การ์ด (ค่าเริ่มต้นเป็น absolute ทับอยู่บนการ์ด) */
  ".carousel .control-dots": {
    position: "static",
    margin: "18px 0 0",
    padding: 0,
  },
}));

/** ช่องว่างซ้าย-ขวาของการ์ดในสไลด์ — เป็นที่ยืนของปุ่มลูกศร */
export const SlidePadding = styled(Box)(({ theme }) => ({
  padding: "0 56px",
  [theme.breakpoints.down("md")]: {
    padding: "0 38px",
  },
}));

/** จุดบอกสไลด์ — ตัวที่เลือกอยู่เป็นแท่งยาว */
export const CarouselDot = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== "selected",
})<{ selected?: boolean }>(
  ({ theme, selected }) => ({
    display: "inline-block",
    width: selected ? "28px" : "7px",
    height: "7px",
    borderRadius: "999px",
    backgroundColor: selected ? colors.themeMainColor : "#D9DEE8",
    margin: "0 4px",
    cursor: "pointer",
    transition: "0.25s",
  })
);

/** ปุ่มย้อนกลับด้านบนหน้ารายละเอียด */
export const BackPill = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: colors.white,
  border: `1px solid ${colors.black_08}`,
  boxShadow: "0 2px 8px rgba(20, 55, 138, 0.06)",
  borderRadius: "999px",
  padding: "8px 18px",
  fontFamily: "Kanit",
  fontSize: "15px",
  color: colors.textPrimary,
  cursor: "pointer",
  transition: "0.2s",
  ":hover": {
    color: colors.themeMainColor,
    borderColor: colors.themeMainColor,
  },
}));

/** หัวเรื่องกลางหน้ารายละเอียด */
export const PageHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Kanit",
  fontSize: "24px",
  fontWeight: 600,
  color: colors.textPrimary,
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    fontSize: "20px",
  },
}));

/** เนื้อหา CKEditor ในหน้ารายละเอียด */
export const AnnounceRichText = styled(Box)(({ theme }) => ({
  fontFamily: "Kanit",
  fontSize: "16px",
  lineHeight: 1.9,
  color: colors.textPrimary,
  wordBreak: "break-word",
  "& p": { marginBottom: "8px" },
  "& img": { maxWidth: "100%", height: "auto", borderRadius: "8px" },
  "& table": { maxWidth: "100%" },
}));
